// src/components/Campaigns/CampaignDetailsDialog.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Chip,
    LinearProgress,
} from "@mui/material";
import { Close as CloseIcon, Edit as EditIcon, Save as SaveIcon, PhotoCamera } from "@mui/icons-material";
import { toast } from "react-toastify";
import baseURL from "../../config";
import COLORS from "../../../../../constants/colors";
import { formatCurrency } from "./campaignsUtils";
import DonationsList from "./DonationsList";
import ShareCampaignSection from "./ShareCampaignSection";
import MDEditor from "@uiw/react-md-editor"; // 1. Import MDEditor
import "@uiw/react-md-editor/markdown-editor.css"; // 1. Import CSS
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

const CampaignDetailsDialog = ({ open, onClose, campaign, onCampaignUpdated }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        goal: "",
    });
    const [loading, setLoading] = useState(false);
    const [newImage, setNewImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);

    const [donations, setDonations] = useState([]);
    const [donationsLoading, setDonationsLoading] = useState(true);

    useEffect(() => {
        if (campaign) {
            setFormData({
                name: campaign.name || "",
                description: campaign.description || "",
                goal: campaign.goal || "",
            });
            setIsEditing(false);
            setNewImage(null);
            setImagePreview(null);
        }
    }, [campaign]);

    useEffect(() => {
        const fetchDonations = async () => {
            if (!campaign || !open || isEditing) {
                setDonations([]);
                setDonationsLoading(false);
                return;
            }

            setDonationsLoading(true);
            try {
                const response = await fetch(`${baseURL}/user/campaigns/${campaign.id}/donations`, {
                    method: "GET",
                    credentials: "include",
                });

                const data = await response.json();
                if (response.ok) {
                    setDonations(data);
                } else {
                    console.error("Error fetching donations:", data.message);
                    toast.error(`Failed to load donations: ${data.message}`);
                    setDonations([]);
                }
            } catch (error) {
                console.error("Network error fetching donations:", error);
                toast.error("Network error while fetching donations.");
                setDonations([]);
            } finally {
                setDonationsLoading(false);
            }
        };

        fetchDonations();
    }, [campaign, open, isEditing]);

    // 2. Updated handleChange to handle MDEditor's direct string output
    const handleChange = (e) => {
        // If 'e' is a string, it's coming from MDEditor (which is the description)
        // Otherwise, it's a standard event from a TextField
        if (typeof e === 'string') {
            setFormData((prev) => ({ ...prev, description: e }));
        } else {
            const { name, value } = e.target;
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const allowed = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!allowed.includes(file.type)) {
            toast.error('Only JPG, JPEG, and PNG images are allowed');
            return;
        }
        setNewImage(file);
        setImagePreview(URL.createObjectURL(file));
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            // Upload new image first if one was selected
            if (newImage) {
                const imgFormData = new FormData();
                imgFormData.append("image", newImage);
                const imgRes = await fetch(`${baseURL}/user/campaigns/${campaign.id}/image`, {
                    method: "POST",
                    credentials: "include",
                    body: imgFormData,
                });
                if (!imgRes.ok) {
                    const imgData = await imgRes.json();
                    throw new Error(imgData.error || "Failed to upload image");
                }
            }

            const response = await fetch(`${baseURL}/user/campaigns/${campaign.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                    name: formData.name,
                    description: formData.description,
                    goal: formData.goal,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Failed to update campaign.");
            }

            toast.success("Campaign updated successfully!");
            setIsEditing(false);
            setNewImage(null);
            setImagePreview(null);
            if (onCampaignUpdated) {
                onCampaignUpdated();
            }
        } catch (error) {
            console.error("Error updating campaign:", error);
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    if (!campaign) return null;

    const percentageRaised = campaign.goal > 0 ? (campaign.raised / campaign.goal) * 100 : 0;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1.5 }}>
                <Typography variant="h6" fontWeight="bold">
                    {isEditing ? "Edit Campaign" : "Campaign Details"}
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 3, pb: 2 }}>
                <Grid container spacing={3}>
                    {/* Left Column: Image (static) and Static/Editable Details */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2, borderRadius: 2, overflow: "hidden", border: "1px solid #eee", height: '200px', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
                            {(imagePreview || campaign.image_url) ? (
                                <img
                                    src={imagePreview || campaign.image_url}
                                    alt={campaign.name}
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                            ) : (
                                <Typography variant="body2" color="text.secondary">No Image</Typography>
                            )}
                            {isEditing && (
                                <IconButton
                                    onClick={() => fileInputRef.current?.click()}
                                    sx={{
                                        position: 'absolute', bottom: 8, right: 8,
                                        backgroundColor: 'rgba(0,0,0,0.6)', color: '#fff',
                                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
                                    }}
                                    size="small"
                                >
                                    <PhotoCamera fontSize="small" />
                                </IconButton>
                            )}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/jpeg,image/jpg,image/png"
                                hidden
                                onChange={handleImageSelect}
                            />
                        </Box>
                        {isEditing && newImage && (
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                New image: {newImage.name}
                            </Typography>
                        )}

                        {!isEditing && (
                            <>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    <strong>Created:</strong> {new Date(campaign.created_at).toLocaleDateString()}
                                </Typography>
                                {campaign.status === 'closed' && campaign.closed_at && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        <strong>Closed On:</strong> {new Date(campaign.closed_at).toLocaleDateString()}
                                    </Typography>
                                )}
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    <strong>Status:</strong>{" "}
                                    <Chip label={campaign.status === "open" ? "Active" : "Closed"} color={campaign.status === "open" ? "success" : "error"} size="small" />
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 2, mb: 1 }}>
                                    <strong>Progress:</strong> {percentageRaised.toFixed(1)}% ({formatCurrency(campaign.raised)} / {formatCurrency(campaign.goal)})
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={percentageRaised > 100 ? 100 : percentageRaised}
                                    sx={{
                                        height: 8,
                                        borderRadius: 5,
                                        backgroundColor: "#e0e0e0",
                                        "& .MuiLinearProgress-bar": {
                                            backgroundColor: percentageRaised >= 100 ? '#4caf50' : COLORS.primary,
                                        },
                                    }}
                                />
                            </>
                        )}
                    </Grid>

                    {/* Right Column: Editable Details */}
                    <Grid item xs={12} md={6}>
                        {isEditing ? (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <TextField
                                    label="Campaign Name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    sx={{ '& .MuiInputBase-root': { borderRadius: 2 } }}
                                />

                                {/* 3. MDEditor for Description */}
                                <Box>
                                    <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>Description (Markdown)</Typography>
                                    <div data-color-mode="light">
                                        <MDEditor
                                            value={formData.description}
                                            onChange={handleChange} // MDEditor passes the string directly to handleChange
                                            height={200}
                                        />
                                    </div>
                                </Box>

                                <TextField
                                    label="Goal (UGX)"
                                    name="goal"
                                    type="number"
                                    value={formData.goal}
                                    onChange={handleChange}
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    sx={{ '& .MuiInputBase-root': { borderRadius: 2 } }}
                                />
                            </Box>
                        ) : (
                            <Box>
                                <Typography variant="h5" fontWeight="bold" sx={{ mb: 1, color: COLORS.primary }}>
                                    {campaign.name}
                                </Typography>

                                <Box
                                    sx={{
                                        color: COLORS.textSecondary,
                                        "& p:last-child": { mb: 0 },
                                        "& ul, & ol": { pl: 2 },
                                        textAlign: "left",
                                        lineHeight: 1.7,
                                        mt: 2,
                                        p: 2,
                                        borderRadius: 2,
                                        backgroundColor: "#fafafa", // light background for clarity
                                        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                                    }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            fontWeight: 600,
                                            mb: 1,
                                            color: COLORS.textPrimary,
                                            borderBottom: "2px solid #e0e0e0",
                                            display: "inline-block",
                                            pb: 0.5,
                                        }}
                                    >
                                        Description
                                    </Typography>

                                    <Box sx={{ mt: 1 }}>
                                        <ReactMarkdown
                                            remarkPlugins={[remarkGfm]}
                                            rehypePlugins={[rehypeRaw]}
                                        >
                                            {campaign.description}
                                        </ReactMarkdown>
                                    </Box>
                                </Box>


                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    <strong>Goal:</strong> {formatCurrency(campaign.goal)}
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    <strong>Raised:</strong> {formatCurrency(campaign.raised)}
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    <strong>Donors:</strong> {campaign.donations || 0}
                                </Typography>
                            </Box>
                        )}
                    </Grid>
                </Grid>

                {!isEditing && (
                    <Box sx={{ mt: 4 }}>
                        <DonationsList donations={donations} loading={donationsLoading} />
                    </Box>
                )}

                {/* Embed Code Section (Only when not editing) */}
                {!isEditing && (
                    <Box sx={{ mt: 4, p: 3, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Embed Donation Form
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            Copy the code below and paste it into your website's HTML to display a donation widget for this campaign.
                        </Typography>

                        <Box sx={{
                            position: 'relative',
                            bgcolor: '#282c34',
                            color: '#abb2bf',
                            p: 2,
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            overflowX: 'auto'
                        }}>
                            <pre style={{ margin: 0 }}>
                                {`<script 
  src="${window.location.origin}/yamba-donate.js" 
  data-campaign-id="${campaign.id}" 
  data-width="100%"
></script>`}
                            </pre>
                            <Button
                                size="small"
                                variant="contained"
                                sx={{ position: 'absolute', top: 8, right: 8, textTransform: 'none' }}
                                onClick={() => {
                                    const code = `<script src="${window.location.origin}/yamba-donate.js" data-campaign-id="${campaign.id}" data-width="100%"></script>`;
                                    navigator.clipboard.writeText(code);
                                    toast.success("Embed code copied to clipboard!");
                                }}
                            >
                                Copy
                            </Button>
                        </Box>
                    </Box>
                )}

                {/* Share Campaign Section (Only when not editing) */}
                {!isEditing && (
                    <Box sx={{ mt: 3 }}>
                        <ShareCampaignSection campaignId={campaign.id} campaignName={campaign.name} />
                    </Box>
                )}
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
                {isEditing ? (
                    <>
                        <Button
                            onClick={() => {
                                setIsEditing(false);
                                setNewImage(null);
                                setImagePreview(null);
                                setFormData({
                                    name: campaign.name || "",
                                    description: campaign.description || "",
                                    goal: campaign.goal || "",
                                });
                            }}
                            color="secondary"
                            variant="outlined"
                            sx={{ textTransform: "none", borderRadius: 2 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            color="primary"
                            variant="contained"
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            sx={{ textTransform: "none", borderRadius: 2, boxShadow: 0, backgroundColor: COLORS.primary, color: '#fff', '&:hover': { backgroundColor: COLORS.primaryDark, boxShadow: 0 } }}
                        >
                            {loading ? "Saving..." : "Save Changes"}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            onClick={onClose}
                            color="primary"
                            variant="outlined"
                            sx={{ textTransform: "none", borderRadius: 2 }}
                        >
                            Close
                        </Button>
                        <Button
                            onClick={() => setIsEditing(true)}
                            color="primary"
                            variant="contained"
                            startIcon={<EditIcon />}
                            sx={{ textTransform: "none", borderRadius: 2, boxShadow: 0, backgroundColor: COLORS.primary, color: '#fff', '&:hover': { backgroundColor: COLORS.primaryDark, boxShadow: 0 } }}
                        >
                            Edit Campaign
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CampaignDetailsDialog;